import { useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { boardsServices } from '@services';
import { getCleanedUrlParams, showError } from '@utils';

import { QUERY_KEYS } from 'constants/queryKeys';
import {
  MY_ISSUES_PAGE_SIZE,
  MY_ISSUES_DEFAULT_ORDER,
  MY_ISSUES_DEFAULT_ORDER_BY,
} from 'components/myIssues/constants';
import { useTeams } from 'components/common/QuickTicketCreationForm/hooks';
import { getMyIssuesPayload, handleMyIssuesData } from './utils';

const useMyIssuesOperations = () => {
  const { query, isReady } = useRouter();
  const { activeTeams: teams } = useTeams();
  const {
    page = 1,
    pageSize = MY_ISSUES_PAGE_SIZE,
    order = MY_ISSUES_DEFAULT_ORDER,
    orderBy = MY_ISSUES_DEFAULT_ORDER_BY,
    tab,
    ...rest
  } = query;

  const {
    data: taskStatusesMapperData,
    error: taskStatusesMapperError,
    isFetched: taskStatusesMapperIsFetched,
  } = useQuery(
    [QUERY_KEYS.GET_BOARDS_TASK_STATUSES_KEY],
    () => boardsServices.getBoardsEnum(),
    {
      enabled: isReady,
    }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_TASK_ENUMS_KEY],
    () => boardsServices.getMyTasksEnums(),
    { enabled: isReady }
  );

  const pageSizeCalculated = useMemo(
    () => parseInt(pageSize, 10) || MY_ISSUES_PAGE_SIZE,
    [pageSize]
  );

  const cleanedParams = getCleanedUrlParams({
    page,
    pageSize: pageSizeCalculated,
    order,
    orderBy,
    tab,
    ...rest,
  });

  const payload = getMyIssuesPayload(rest);

  const {
    data: myIssuesData,
    isLoading: isMyIssuesLoading,
    isFetching: isMyIssuesFetching,
    error: myIssuesError,
    refetch: myIssuesDataRefetch,
  } = useQuery(
    [QUERY_KEYS.GET_MY_ISSUES_KEY, cleanedParams, payload],
    () => boardsServices.getMyIssues(cleanedParams, payload),
    {
      enabled: isReady && taskStatusesMapperIsFetched,
      refetchOnMount: true,
    }
  );

  const myIssues = useMemo(
    () =>
      handleMyIssuesData(
        myIssuesData?.data || [],
        taskStatusesMapperData?.data?.taskStatuses || [],
        teams
      ),
    [myIssuesData?.data, teams, taskStatusesMapperData?.data?.taskStatuses]
  );

  const error = useMemo(
    () => myIssuesError || taskStatusesMapperError,
    [myIssuesError, taskStatusesMapperError]
  );

  useEffect(() => {
    if (error) {
      showError(error);
    }
  }, [error]);

  return {
    page,
    enums: enumsResponse?.data?.data || {},
    total: myIssuesData?.meta?.total,
    pageSize: pageSizeCalculated,
    loading: isMyIssuesLoading || isMyIssuesFetching,
    data: myIssues || [],
    refetch: myIssuesDataRefetch,
  };
};

export default useMyIssuesOperations;
